import clsx from 'clsx'
import type { ReactNode } from 'react'
import { useState } from 'react'
import { FormattedMessage } from 'react-intl'

import { Button } from '@/src/elements/button/default'
import { Card, CardBody, CardHeader, CardFooter } from '@/src/elements/card'
import { Icon } from '@/src/elements/media/Icon'
import { Typography } from '@/src/elements/text/Typography'
import { TEST_ID } from '@/src/general/constants/testId'
import { ICON } from '@/src/general/enums/icons'
import { stopPropagation } from '@/src/general/utils/stopPropagation'

import type { TPublicationApi } from '../../../api/types/getPublication.types'
import type { TMergePublicationTypes, TPickPublication } from '../../../models/publication.types'
import { useToggleFavoritePublication } from '../../../services/useFavoritePublication'
import { usePublicationHandlers } from '../PublicationCard/usePublicationHandlers'
import type { TPublicationItemProps } from '../PublicationItem/PublicationItem'
import { PublicationShareCommentButtons } from '../PublicationShareCommentButtons'

export type TPublicationItemContentProps = Omit<TPublicationItemProps, 'publication'> & {
  publication: TMergePublicationTypes<
    [TPublicationItemProps['publication'], TPickPublication<TPublicationApi, 'prayersCount' | 'isFavorite'>]
  >
}
export const PublicationItemContent = ({
  cardClickEvent,
  publication,
  publicationTrackingSource,
  trackingSource,
}: TPublicationItemContentProps): ReactNode => {
  const { hasPrayed, prayersCount } = publication
  const [isFavorite, setIsFavorite] = useState(publication.isFavorite)
  const { mutate: toggleFavorite } = useToggleFavoritePublication()
  const { onSharePress, onCommentPress, onCardPress } = usePublicationHandlers({
    publication,
    trackingSource,
    publicationTrackingSource,
    cardClickEvent,
  })
  return (
    <Card
      onPress={onCardPress}
      isPressable={true}
      additionalProps={{ role: 'link' }}
      className={clsx('p-4 gap-2', { 'bg-success-bg-high': hasPrayed })}
    >
      <CardHeader className="p-0">
        <Typography colors={hasPrayed ? 'success' : 'default'} contrast="high" bold>
          {publication.name}
        </Typography>
      </CardHeader>
      {!!hasPrayed && 'prayersCount' in publication && (
        <CardBody className="p-0 flex-row gap-3">
          <Icon name={ICON.CHECK_CIRCLE_BROKEN} size="md" className="color-success-text-contrast-low" />
          <Typography className="color-success-text-contrast-low">
            <FormattedMessage
              id="publication.prayed-by.short"
              values={{
                count: prayersCount - 1,
              }}
            />
          </Typography>
        </CardBody>
      )}
      <CardFooter
        className={clsx('p-0 gap-2', {
          'flex-row justify-between': hasPrayed,
          'flex-col': !hasPrayed,
        })}
      >
        <PublicationShareCommentButtons
          publication={publication}
          onSharePress={onSharePress}
          onCommentPress={onCommentPress}
          colors={hasPrayed ? 'success' : 'default'}
        />
        {!hasPrayed && (
          <Button
            variant="solid"
            colors="primary"
            fullWidth
            startIcon={ICON.FIRE}
            onPress={stopPropagation(onCardPress)}
          >
            <FormattedMessage id="pray.pray" />
          </Button>
        )}
        {hasPrayed && 'isFavorite' in publication && (
          <Button
            variant="light"
            colors="success"
            size="sm"
            fullWidth
            isIconOnly
            testID={TEST_ID.PUBLICATION_CARD.BOOKMARK_BUTTON}
            startIcon={isFavorite ? ICON.BOOKMARK_FILL : ICON.BOOKMARK}
            onPress={stopPropagation(() => {
              toggleFavorite({ publicationId: publication.id, action: isFavorite ? 'remove' : 'add' })
              setIsFavorite(!isFavorite)
            })}
          />
        )}
      </CardFooter>
    </Card>
  )
}
